import { useState } from 'react';
import { api, ApiError, type Branding } from '../lib/api.ts';
import { Mark } from './Mark.tsx';

interface Props {
  readonly branding: Branding | null;
  readonly onSignedIn: () => void;
  /** Offered on a phone: enrol as a site device instead. */
  readonly onEnrol?: () => void;
}

/**
 * Two-step sign-in mirroring the API: password first, then TOTP against
 * a short-lived challenge. The challenge is held in component state and
 * never persisted -- it expires in five minutes and nothing should
 * outlive that.
 */
type Stage = 'password' | 'totp';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'Email or password is incorrect.',
  invalid_code: 'That code is not valid. Check your authenticator app.',
  challenge_invalid: 'Sign-in timed out. Please start again.',
  setup_required: 'This account needs first-time setup.',
  totp_enrolment_required: 'Two-factor setup is incomplete.',
};

export const Login = ({ branding, onSignedIn, onEnrol }: Props) => {
  const [stage, setStage] = useState<Stage>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const [challenge, setChallenge] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const fail = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : 'unknown_error';
    setError(MESSAGES[code] ?? 'Something went wrong. Please try again.');
  };

  const submitPassword = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api.login(email.trim(), password);
      // No challenge means the server already signed us in: the demo
      // tenant skips the second factor, and so does a browser still
      // inside its 90-day trust window. Walking on to the code step
      // regardless stranded a signed-in user at a prompt with no
      // answer, because no authenticator was ever enrolled.
      if (!result.challenge) {
        onSignedIn();
        return;
      }
      setChallenge(result.challenge);
      setStage('totp');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    setBusy(true);
    setError('');
    try {
      await api.totp(challenge, code.replace(/\D/g, ''), trust);
      onSignedIn();
    } catch (e) {
      fail(e);
      // An expired or rejected challenge cannot be retried -- send the
      // user back rather than letting them hammer a dead token.
      if (e instanceof ApiError && e.code === 'challenge_invalid') {
        setStage('password');
        setPassword('');
        setCode('');
      }
    } finally {
      setBusy(false);
    }
  };

  const name = branding?.identity?.product_name ?? branding?.name ?? '';

  return (
    <main className="loginWrap">
      <div className="loginCard">
        <Mark size={42} />
        <h1>{name}</h1>
        {/* The hostname, as the app header shows it: someone with two
            clients open needs to know which one they are signing
            into before they type a password. */}
        <p className="mono loginHost">{window.location.hostname}</p>
        <p className="mono">
          {stage === 'password' ? 'Sign in' : 'Two-factor code'}
        </p>

        {error && <p className="err">{error}</p>}

        {stage === 'password' ? (
          <>
            <label>
              <span className="mono">Email</span>
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              <span className="mono">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitPassword()}
              />
            </label>
            <button
              className="primary"
              disabled={busy || !email || !password}
              onClick={submitPassword}
            >
              {busy ? 'Checking...' : 'Continue'}
            </button>
          </>
        ) : (
          <>
            <label>
              <span className="mono">6-digit code</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={code}
                autoFocus
                className="otp"
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitCode()}
              />
            </label>

            <label className="check trustPick">
              <input
                type="checkbox"
                checked={trust}
                onChange={(e) => setTrust(e.target.checked)}
              />
              <span>
                Trust this device for 90 days
                <em>
                  Leave unticked on a shared or counter machine.
                </em>
              </span>
            </label>
            <button
              className="primary"
              disabled={busy || code.replace(/\D/g, '').length !== 6}
              onClick={submitCode}
            >
              {busy ? 'Verifying...' : 'Sign in'}
            </button>
          </>
        )}
        {onEnrol && (
          <button className="linkish" onClick={onEnrol}>
            Set this phone up as a site device
          </button>
        )}
      </div>
    </main>
  );
};
